/**
 * Almacen minimo de tokens. Vive fuera de React para que el cliente HTTP
 * pueda leerlo sin depender del arbol de componentes (y sin ciclos de import).
 *
 * Sobre guardar el refresh token en localStorage: es el compromiso estandar
 * de una SPA sin backend de sesion. La alternativa (cookie httpOnly) exige
 * un dominio comun y un endpoint de sesion que hoy no existen. Mitigado con
 * revocacion activada en el App Client y validez de 30 dias.
 */

const STORAGE_KEY = 'alexiarest.idToken';
const REFRESH_KEY = 'alexiarest.refreshToken';

let memoryToken: string | null = null;
let memoryRefresh: string | null = null;

export function getToken(): string | null {
  if (memoryToken) return memoryToken;
  try {
    memoryToken = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    memoryToken = null;
  }
  return memoryToken;
}

export function setToken(token: string | null): void {
  memoryToken = token;
  try {
    if (token) window.localStorage.setItem(STORAGE_KEY, token);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Modo privado o almacenamiento bloqueado: seguimos solo en memoria.
  }
}

export function getRefreshToken(): string | null {
  if (memoryRefresh) return memoryRefresh;
  try {
    memoryRefresh = window.localStorage.getItem(REFRESH_KEY);
  } catch {
    memoryRefresh = null;
  }
  return memoryRefresh;
}

export function setRefreshToken(token: string | null): void {
  memoryRefresh = token;
  try {
    if (token) window.localStorage.setItem(REFRESH_KEY, token);
    else window.localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* solo memoria */
  }
}

/**
 * Renovacion compartida entre el AuthContext (proactiva) y el cliente HTTP
 * (reactiva ante un 401). Se registra desde el AuthContext para no crear un
 * ciclo de imports auth ⇄ api. Debe devolver true si hay token nuevo.
 */
let refresher: (() => Promise<boolean>) | null = null;
let refreshing: Promise<boolean> | null = null;

export function registerTokenRefresher(fn: (() => Promise<boolean>) | null): void {
  refresher = fn;
}

/** Renueva una sola vez aunque lo pidan varias peticiones a la vez. */
export function refreshSession(): Promise<boolean> {
  if (!refresher) return Promise.resolve(false);
  if (!refreshing) {
    refreshing = refresher().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

export interface TokenClaims {
  sub?: string;
  email?: string;
  name?: string;
  exp?: number;
  'custom:tenantId'?: string;
  'custom:role'?: string;
}

/** Decodifica el payload de un JWT sin verificar la firma (eso es del backend). */
export function decodeClaims(token: string): TokenClaims | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      window
        .atob(normalized)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
    return JSON.parse(json) as TokenClaims;
  } catch {
    return null;
  }
}

export function isExpired(claims: TokenClaims | null): boolean {
  if (!claims?.exp) return false;
  return claims.exp * 1000 <= Date.now();
}
